import { Badge } from '@react/components';
import type { AspectCardData } from '@/features/aspects/types';

interface AspectCardProps {
  data: AspectCardData;
  onNavigate: (data: AspectCardData) => void;
}

export function AspectCard({ data, onNavigate }: AspectCardProps) {
  return (
    <div
      className="group p-3 rounded-lg border border-rail-border hover:border-primary/30 hover:bg-accent/30 cursor-pointer transition-colors"
      onClick={() => onNavigate(data)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.target === e.currentTarget && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault();
          onNavigate(data);
        }
      }}
    >
      <div className="flex items-start gap-3">
        <div
          className="h-8 w-8 rounded-md flex items-center justify-center flex-shrink-0"
          style={{ backgroundColor: data.color ? `color-mix(in srgb, currentColor 10%, transparent)` : undefined }}
        >
          <svg className={`h-4 w-4 ${data.color}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
            <path d={data.icon} />
          </svg>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <p className="text-sm font-medium text-rail-foreground truncate">{data.title}</p>
            {data.status && (
              <Badge variant={data.statusVariant as any} size="sm" className="flex-shrink-0">
                {data.status}
              </Badge>
            )}
          </div>
          {data.subtitle && (
            <p className="text-xs text-muted-foreground truncate mt-0.5">{data.subtitle}</p>
          )}
          {/* Type-specific preview */}
          <TypeSpecificPreview typeName={data.typeName} fields={data.previewFields} />
        </div>
      </div>
    </div>
  );
}

function TypeSpecificPreview({ typeName: _typeName, fields }: { typeName: string; fields: Record<string, string> }) {
  const entries = Object.entries(fields);
  if (entries.length === 0) return null;

  return (
    <div className="flex items-center gap-2 mt-1.5 flex-wrap">
      {entries.map(([key, value]) => (
        <span key={key} className="text-xs text-muted-foreground">
          <span className="font-medium">{key}:</span> {value}
        </span>
      ))}
    </div>
  );
}
